import React, { useCallback, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  LinearProgress,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Close as CloseIcon,
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import { axiosService } from 'shared/helpers/axios';

const STEPS = ['Sources', 'Review & Edit', 'Done'];

const STATUS_COLOR = { open: 'success', upcoming: 'warning', closed: 'default', cancelled: 'error' };

function SourcesStep({ files, setFiles, urls, setUrls, urlInput, setUrlInput }) {
  const fileRef = useRef();

  const addUrl = () => {
    const trimmed = urlInput.trim();
    if (trimmed && !urls.includes(trimmed)) setUrls(prev => [...prev, trimmed]);
    setUrlInput('');
  };

  const onDrop = useCallback((e) => {
    e.preventDefault();
    const dropped = Array.from(e.dataTransfer.files).filter(f =>
      f.name.match(/\.(pdf|docx?)/i)
    );
    setFiles(prev => [...prev, ...dropped]);
  }, [setFiles]);

  return (
    <Stack spacing={3}>
      {/* File upload */}
      <Box>
        <Typography variant="subtitle2" gutterBottom>Upload PDF / DOCX files</Typography>
        <Box
          onDrop={onDrop}
          onDragOver={e => e.preventDefault()}
          onClick={() => fileRef.current?.click()}
          sx={{
            border: '2px dashed',
            borderColor: 'divider',
            borderRadius: 1,
            p: 3,
            textAlign: 'center',
            cursor: 'pointer',
            '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' },
          }}
        >
          <UploadIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
          <Typography variant="body2" color="text.secondary">
            Drag & drop files here, or click to browse
          </Typography>
          <Typography variant="caption" color="text.disabled">
            PDF and DOCX, up to 20 MB each
          </Typography>
          <input
            ref={fileRef}
            type="file"
            multiple
            accept=".pdf,.docx,.doc"
            style={{ display: 'none' }}
            onChange={e => setFiles(prev => [...prev, ...Array.from(e.target.files)])}
          />
        </Box>
        {files.length > 0 && (
          <Stack spacing={0.5} mt={1}>
            {files.map((f, i) => (
              <Stack key={i} direction="row" alignItems="center" spacing={1}>
                <Typography variant="body2" sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {f.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {(f.size / 1024 / 1024).toFixed(1)} MB
                </Typography>
                <IconButton size="small" onClick={() => setFiles(prev => prev.filter((_, j) => j !== i))}>
                  <CloseIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Stack>
            ))}
          </Stack>
        )}
      </Box>

      <Divider>or</Divider>

      {/* URL input */}
      <Box>
        <Typography variant="subtitle2" gutterBottom>Add call page URLs</Typography>
        <Stack direction="row" spacing={1}>
          <TextField
            value={urlInput}
            onChange={e => setUrlInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && addUrl()}
            placeholder="https://example.org/funding-call"
            size="small"
            fullWidth
          />
          <Button variant="outlined" startIcon={<AddIcon />} onClick={addUrl} sx={{ whiteSpace: 'nowrap' }}>
            Add
          </Button>
        </Stack>
        <Stack spacing={0.5} mt={1}>
          {urls.map((u, i) => (
            <Stack key={i} direction="row" alignItems="center" spacing={1}>
              <Typography variant="body2" sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'primary.main' }}>
                {u}
              </Typography>
              <IconButton size="small" onClick={() => setUrls(prev => prev.filter((_, j) => j !== i))}>
                <CloseIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Stack>
          ))}
        </Stack>
      </Box>
    </Stack>
  );
}

function ReviewStep({ records, selected, setSelected, onEdit }) {
  const toggleAll = (checked) => {
    if (checked) setSelected(new Set(records.map((_, i) => i)));
    else setSelected(new Set());
  };
  const toggle = (i) => setSelected(prev => {
    const next = new Set(prev);
    next.has(i) ? next.delete(i) : next.add(i);
    return next;
  });
  const allChecked = selected.size === records.length;

  if (records.length === 0) return (
    <Alert severity="warning">No calls were extracted from the provided sources.</Alert>
  );

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {selected.size} of {records.length} calls selected for import. Uncheck any you want to skip.
      </Typography>
      <TableContainer sx={{ maxHeight: 420, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  checked={allChecked}
                  indeterminate={selected.size > 0 && !allChecked}
                  onChange={e => toggleAll(e.target.checked)}
                  size="small"
                />
              </TableCell>
              <TableCell>Title</TableCell>
              <TableCell>Agency</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Deadline</TableCell>
              <TableCell>Source</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {records.map((rec, i) => {
              const call = rec.call || {};
              const agency = rec.agency || {};
              const hasError = rec.error && !call.title;
              return (
                <TableRow key={i} sx={{ opacity: selected.has(i) ? 1 : 0.45 }}>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selected.has(i)}
                      onChange={() => toggle(i)}
                      size="small"
                      disabled={hasError}
                    />
                  </TableCell>
                  <TableCell>
                    {hasError
                      ? <Typography variant="caption" color="error">{rec.error}</Typography>
                      : <Tooltip title={call.description || ''} placement="top">
                          <Typography variant="body2" sx={{ maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {call.title || '—'}
                          </Typography>
                        </Tooltip>
                    }
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption">{agency.name || '—'}</Typography>
                  </TableCell>
                  <TableCell>
                    {call.status && (
                      <Chip label={call.status} size="small" color={STATUS_COLOR[call.status] || 'default'} />
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption">
                      {call.closes_at ? call.closes_at.slice(0, 10) : '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Tooltip title={rec.source || ''}>
                      <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 100, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>
                        {rec.source_type === 'url' ? '🔗 URL' : '📄 ' + (rec.source || '')}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    {!hasError && (
                      <IconButton size="small" onClick={() => onEdit(i)}>
                        <DeleteIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

function DoneStep({ stats }) {
  if (!stats) return null;
  return (
    <Stack spacing={2} alignItems="center" sx={{ py: 2 }}>
      <Typography variant="h5" fontWeight={700} color="success.main">Import Complete</Typography>
      <Stack direction="row" spacing={3}>
        <Box textAlign="center">
          <Typography variant="h4" fontWeight={700} color="success.main">{stats.created}</Typography>
          <Typography variant="caption" color="text.secondary">Created</Typography>
        </Box>
        <Box textAlign="center">
          <Typography variant="h4" fontWeight={700} color="info.main">{stats.updated}</Typography>
          <Typography variant="caption" color="text.secondary">Updated</Typography>
        </Box>
        <Box textAlign="center">
          <Typography variant="h4" fontWeight={700} color="text.secondary">{stats.skipped}</Typography>
          <Typography variant="caption" color="text.secondary">Skipped</Typography>
        </Box>
      </Stack>
      {stats.errors?.length > 0 && (
        <Alert severity="warning" sx={{ width: '100%' }}>
          {stats.errors.length} call(s) failed to import:
          <ul style={{ margin: '4px 0 0', paddingLeft: 16 }}>
            {stats.errors.map((e, i) => (
              <li key={i}><strong>{e.title}</strong>: {e.error}</li>
            ))}
          </ul>
        </Alert>
      )}
    </Stack>
  );
}

export default function ImportCallsDialog({ open, onClose, onImported }) {
  const [step, setStep] = useState(0);
  const [files, setFiles] = useState([]);
  const [urls, setUrls] = useState([]);
  const [urlInput, setUrlInput] = useState('');
  const [records, setRecords] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [extractErrors, setExtractErrors] = useState([]);
  const [importStats, setImportStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [updateExisting, setUpdateExisting] = useState(false);

  const reset = () => {
    setStep(0); setFiles([]); setUrls([]); setUrlInput('');
    setRecords([]); setSelected(new Set()); setExtractErrors([]);
    setImportStats(null); setLoading(false); setError(null);
  };

  const handleClose = () => { reset(); onClose(); };

  const handleExtract = async () => {
    if (files.length === 0 && urls.length === 0) {
      setError('Add at least one file or URL before extracting.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const formData = new FormData();
      files.forEach(f => formData.append('files[]', f));
      formData.append('urls', JSON.stringify(urls));
      const res = await axiosService.post('/funding/calls/batch-extract/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const extracted = res.data.results || [];
      const errs = res.data.errors || [];
      // Filter out records with no call title
      const valid = extracted.filter(r => r.call?.title || r.error);
      setRecords(valid);
      setExtractErrors(errs);
      // Auto-select records that have a title (no error)
      setSelected(new Set(valid.map((r, i) => r.call?.title ? i : null).filter(i => i !== null)));
      setStep(1);
    } catch (e) {
      setError(e.response?.data?.error || 'Extraction failed. Check AI settings.');
    } finally {
      setLoading(false);
    }
  };

  const handleImport = async () => {
    const toImport = records.filter((_, i) => selected.has(i) && records[i].call?.title);
    if (toImport.length === 0) { setError('Select at least one call to import.'); return; }
    setLoading(true);
    setError(null);
    try {
      const res = await axiosService.post('/funding/calls/import-batch/', {
        records: toImport,
        update_existing: updateExisting,
      });
      setImportStats(res.data);
      setStep(2);
      onImported?.();
    } catch (e) {
      setError(e.response?.data?.error || 'Import failed.');
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = (i) => {
    setRecords(prev => prev.filter((_, j) => j !== i));
    setSelected(prev => {
      const next = new Set();
      prev.forEach(v => { if (v < i) next.add(v); else if (v > i) next.add(v - 1); });
      return next;
    });
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6" fontWeight={700}>Import Calls via AI</Typography>
        <IconButton size="small" onClick={handleClose}><CloseIcon /></IconButton>
      </DialogTitle>

      <DialogContent>
        <Stepper activeStep={step} sx={{ mb: 3 }}>
          {STEPS.map(label => (
            <Step key={label}><StepLabel>{label}</StepLabel></Step>
          ))}
        </Stepper>

        {loading && <LinearProgress sx={{ mb: 2 }} />}
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
        {extractErrors.length > 0 && step === 1 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {extractErrors.length} source(s) failed to extract:
            <ul style={{ margin: '4px 0 0', paddingLeft: 16 }}>
              {extractErrors.map((e, i) => <li key={i}><strong>{e.source}</strong>: {e.error}</li>)}
            </ul>
          </Alert>
        )}

        {step === 0 && (
          <SourcesStep
            files={files} setFiles={setFiles}
            urls={urls} setUrls={setUrls}
            urlInput={urlInput} setUrlInput={setUrlInput}
          />
        )}
        {step === 1 && (
          <ReviewStep
            records={records}
            selected={selected}
            setSelected={setSelected}
            onEdit={handleRemove}
          />
        )}
        {step === 2 && <DoneStep stats={importStats} />}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        {step === 0 && (
          <>
            <Button onClick={handleClose}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleExtract}
              disabled={loading || (files.length === 0 && urls.length === 0)}
              startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon />}
            >
              {loading ? 'Extracting…' : 'Extract'}
            </Button>
          </>
        )}
        {step === 1 && (
          <>
            <Button onClick={() => setStep(0)} disabled={loading}>Back</Button>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 'auto' }}>
              <Checkbox
                size="small"
                checked={updateExisting}
                onChange={e => setUpdateExisting(e.target.checked)}
                id="update-existing"
              />
              <Typography component="label" htmlFor="update-existing" variant="body2" sx={{ cursor: 'pointer' }}>
                Update existing calls
              </Typography>
              <Button
                variant="contained"
                onClick={handleImport}
                disabled={loading || selected.size === 0}
                startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
              >
                {loading ? 'Importing…' : `Import ${selected.size} call${selected.size !== 1 ? 's' : ''}`}
              </Button>
            </Box>
          </>
        )}
        {step === 2 && (
          <Button variant="contained" onClick={handleClose}>Done</Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
